import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Spin,
  Card,
  Typography,
  Divider,
  Switch,
  Space,
  Col,
  Row,
  Radio,
  Select,
} from "antd";
import {
  fetchUserById,
  updateUser,
  sendPasswordResetEmail,
} from "../../../../services/users";
import {
  UserDesignationSelector,
  UserRoleSelector,
} from "../../../../components/selector";
import useGoBackOrDefault from "../../../../hooks/useDefaultNavigation";
import {
  MailOutlined,
  UserOutlined,
  LockOutlined,
  IdcardOutlined,
  SolutionOutlined,
  ClusterOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import _, { omit } from "lodash";
import { isEqualOnCommonFields } from "../../../../utils";
import { useNotification } from "../../../../components/notification.provider";
import {
  fetchDistricts,
  fetchHospitalsByDistrictId,
} from "../../../../services/districts";

const { Title, Text } = Typography;

const UserEditPage = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [user, setUser] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  const isWatchedOtherDesignation = Form.useWatch("designation", form);
  const isWatchedDistrict = Form.useWatch("districtUid", form);

  const goBackToIndexOrBack = useGoBackOrDefault({
    defaultUrl: "/manage/auth/users",
  });

  const notification = useNotification();

  const loadUser = async () => {
    try {
      const data = await fetchUserById(uid);
      setUser(data);
      const values = {
        ...data,
        roles: data?.roles,
        gender: data?.gender,
        hospitalUid: data?.hospital?.uid,
        districtUid: data?.district?.uid,
      };
      form.setFieldsValue(values);
      setInitialValues(values);
    } catch (err) {
      notification.error({
        message: "Failed to load user data",
        description: err.message,
      });
      console.error("Loading failed", err);
    } finally {
      setLoading(false);
    }
  };

  const loadDistricts = async () => {
    try {
      // setLoading(true);
      const data = await fetchDistricts();
      setDistricts(data);
    } catch (err) {
      notification.error({
        message: "Failed to load districts data",
        description: err.message,
      });
      console.error("Loading failed", err);
    } finally {
      setLoading(false);
    }
  };

  const loadHospitalsByDistrictId = async (districtId) => {
    try {
      // setLoading(true);
      const data = await fetchHospitalsByDistrictId(districtId);
      setHospitals(data);
    } catch (err) {
      notification.error({
        message: "Failed to load hospitals data",
        description: err.message,
      });
      console.error("Loading failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    loadDistricts();
  }, [uid, form]);

  useEffect(() => {
    if (isWatchedDistrict) {
      const currentDistrict = form.getFieldValue("districtUid");
      if (currentDistrict) {
        loadHospitalsByDistrictId(currentDistrict);
      }
    }
  }, [isWatchedDistrict, form]);

  const handleFieldChange = () => {
    const formValues = form.getFieldsValue();
    const touched = form.isFieldsTouched(false);
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);

    // Compare current form values with initial values
    const isNotChanged =
      isEqualOnCommonFields(formValues, initialValues) &&
      !formValues.password &&
      !formValues.confirmPassword;

    // Enable submit if form is touched, has no errors, and is changed
    setIsFormSubmittable(touched && !hasErrors && !isNotChanged);
  };

  const onFinish = async (values) => {
    try {
      setUpdating(true);
      const updatedRecord = omit(values, [
        "roles",
        "password",
        "confirmPassword",
      ]);
      const userRole = values.roles.at(0)["roleName"] || "ROLE_USER";
      const updatedData = {
        ...updatedRecord,
        uid,
        role: userRole,
        // avatarUrl: avatarPreview || null,
      };

      // If password is set, include it in update
      let passwordChanged = false;
      if (values.password) {
        updatedData.password = values.password;
        setNewPassword(values.password);
        passwordChanged = true;
      }

      await updateUser(updatedData);
      notification.success({ message: "User updated successfully!" });
      // Update initial values after successful update
      setInitialValues(updatedData);
      setIsFormSubmittable(false);

      if (passwordChanged) {
        setShowPasswordPrompt(true);
      } else {
        navigate("/manage/auth/users");
      }
    } catch (err) {
      notification.error({
        message: "Update failed",
        description: err.message,
      });
      console.error("Update failed", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleSendPasswordReset = async () => {
    try {
      await sendPasswordResetEmail(user.email);
      notification.success({
        message: "Password reset email sent successfully!",
      });
    } catch (err) {
      notification.error({
        message: "Failed to send password reset email",
        description: err.message,
      });
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "100px" }}
      >
        <Spin tip="Loading user..." size="large" />
      </div>
    );
  }

  return (
    <Card
      variant={false}
      style={{
        width: "100%",
        margin: "24px auto",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        borderRadius: 8,
        padding: "0 32px",
        minWidth: 320,
        maxWidth: "1200px",
      }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 8 }} />
          Edit User Profile
        </Title>
      }
    >
      {/* Password Change Prompt */}
      {showPasswordPrompt && (
        <div style={{ marginBottom: 24 }}>
          <Card
            type="inner"
            title="Password Changed"
            style={{ background: "#fffbe6", borderColor: "#ffe58f" }}
          >
            <Text strong>The user's password has been changed.</Text>
            <br />
            <Text>
              Please send a password reset email to the user and communicate the
              new password below:
            </Text>
            <br />
            <Text copyable code style={{ fontSize: 18 }}>
              {newPassword}
            </Text>
            <br />
            <Button
              type="primary"
              style={{ marginTop: 12 }}
              onClick={() => {
                setShowPasswordPrompt(false);
                navigate("/manage/auth/users");
              }}
            >
              OK
            </Button>
          </Card>
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={handleFieldChange}
        style={{ padding: "0 16px" }}
      >
        <Row gutter={24}>
          {/* Basic Info */}
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="John" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              validateDebounce={1000}
              rules={[{ required: true, message: "Please input last name" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Doe" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: "Please input username" }]}
            >
              <Input
                prefix={<IdcardOutlined />}
                placeholder="johndoe"
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              validateDebounce={1000}
              rules={[
                {
                  required: true,
                  message: "Please enter user's email address",
                },
                { type: "email", message: "Please enter valid email" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="john@example.com" />
            </Form.Item>
          </Col>

          {/* Password Change Fields */}
          <Col xs={24} md={12}>
            <Form.Item
              label="New Password"
              name="password"
              validateDebounce={1000}
              rules={[
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Leave blank to keep unchanged"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              validateDebounce={1000}
              dependencies={["password"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!form.getFieldValue("password") && !value) {
                      return Promise.resolve();
                    }
                    if (form.getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject("The two passwords do not match!");
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Gender"
              name="gender"
              validateFirst
              hasFeedback
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Radio.Group>
                <Radio value="MALE">Male</Radio>
                <Radio value="FEMALE">Female</Radio>
                {/* <Radio value="OTHER">Other</Radio> */}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="District"
              name="districtUid"
              validateFirst
              hasFeedback
              rules={[{ required: true, message: "Please select district" }]}
            >
              <Select
                placeholder="Select district..."
                loading={loading}
                prefix={<ClusterOutlined />}
              >
                {districts?.map((district) => (
                  <Select.Option key={district.uid} value={district.uid}>
                    {district.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {isWatchedDistrict && (
            <Col xs={24} md={12}>
              <Form.Item
                label="Hospital"
                name="hospitalUid"
                validateFirst
                hasFeedback
                rules={[{ required: true, message: "Please select hospital" }]}
              >
                <Select
                  placeholder="Select hospital..."
                  loading={loading}
                  prefix={<MedicineBoxOutlined />}
                >
                  {hospitals?.map((hospital) => (
                    <Select.Option key={hospital?.uid} value={hospital?.uid}>
                      {hospital?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* Role and Designation */}
          <Col span={24}>
            <Divider orientation="left" style={{ color: "#666" }}>
              User Designation & Privileges
            </Divider>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Designation"
              name="designation"
              tooltip="User's professional title or position"
              rules={[
                {
                  required: true,
                  message:
                    "Please select user's designation. Specify OTHER if designation does not exist",
                },
              ]}
            >
              <UserDesignationSelector />
            </Form.Item>
          </Col>
          {isWatchedOtherDesignation == "OTHER" && (
            <Form.Item
              label="Custom Designation"
              name="customDesignation"
              tooltip="User's specific designaton"
            >
              <Input
                prefix={<SolutionOutlined />}
                placeholder="Please specify designation"
              />
            </Form.Item>
          )}
          <Col xs={24} md={12}>
            <Form.Item
              label="Role"
              name="roles"
              tooltip="Determines system access permissions"
              rules={[
                { required: true, message: "Please select just one role" },
              ]}
            >
              <UserRoleSelector />
            </Form.Item>
          </Col>

          {/* Account Actions */}
          <Col span={24}>
            <Divider orientation="left" style={{ color: "#666" }}>
              Account Actions
            </Divider>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="default"
                icon={<LockOutlined />}
                onClick={handleSendPasswordReset}
                style={{ width: "100%", textAlign: "left" }}
              >
                Send Password Reset Email
              </Button>
              <Form.Item
                name="enabled"
                label="Account Status"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Disabled"
                  defaultChecked={user?.enabled}
                />
              </Form.Item>
            </Space>
          </Col>

          {/* Form Actions */}
          <Col span={24} style={{ marginTop: 24 }}>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updating}
                  size="large"
                  disabled={!isFormSubmittable}
                >
                  Update User
                </Button>
                <Button onClick={goBackToIndexOrBack} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default UserEditPage;